import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { formatEURDecimal, formatDate } from "../lib/format";
import { Camera, TrendingUp, TrendingDown, Trash2 } from "lucide-react";
import { toast } from "sonner";

const RANGES = [
  { value: 12, label: "12 semanas" },
  { value: 26, label: "26 semanas" },
  { value: 52, label: "1 año" },
  { value: 104, label: "2 años" },
  { value: 0, label: "Todo" },
];

export default function Snapshots() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [range, setRange] = useState(0);
  const [data, setData] = useState({ snapshots: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    const q = range ? `?year=${year}&weeks=${range}` : `?year=${year}`;
    api.get(`/snapshots${q}`).then(r => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, range]);

  const create = async () => {
    setCreating(true);
    try {
      await api.post(`/snapshots?year=${year}`);
      toast.success("Snapshot creado");
      load();
    } catch (e) { toast.error("Error"); }
    finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este snapshot?")) return;
    try { await api.delete(`/snapshots/${id}`); load(); }
    catch (e) { toast.error("Error"); }
  };

  const snaps = data.snapshots || [];
  const last = snaps[snaps.length - 1];
  const prev = snaps[snaps.length - 2];
  const delta = (last && prev) ? last.cashflow_fin_anio - prev.cashflow_fin_anio : 0;

  // Build chart data
  const maxAbs = useMemo(() => {
    if (!snaps.length) return 1;
    const arr = snaps.flatMap(s => [s.cashflow_fin_anio, s.saldo_bancos]);
    return Math.max(...arr.map(v => Math.abs(v)), 1);
  }, [snaps]);

  return (
    <div className="p-8" data-testid="snapshots-page">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">Histórico</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Evolución del cashflow.</h1>
          <p className="text-sm text-[var(--ink-muted)] mt-2 max-w-2xl">
            Snapshot semanal automático cada lunes a las 09:00 (UTC+1) + manual cuando quieras. Compara la proyección
            a fin de año, el saldo de bancos y el estado de facturas semana a semana.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="snap-year" className="border border-[var(--border)] bg-white px-3 py-2 text-sm font-mono-num focus:outline-none focus:border-black" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[year-2, year-1, year, year+1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select data-testid="snap-range" className="border border-[var(--border)] bg-white px-3 py-2 text-sm font-mono-num focus:outline-none focus:border-black" value={range} onChange={(e) => setRange(Number(e.target.value))}>
            {RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button onClick={create} disabled={creating} className="bg-black text-white px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-gray-800 disabled:opacity-40" data-testid="snap-create">
            <Camera size={14} /> {creating ? "Guardando…" : "Snapshot ahora"}
          </button>
        </div>
      </div>

      {/* Last vs prev KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="border border-[var(--border)] bg-white p-5" data-testid="kpi-last">
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Último snapshot</div>
          <div className="font-display font-black text-xl mt-1">{last ? formatEURDecimal(last.cashflow_fin_anio) : "—"}</div>
          <div className="text-[10px] font-mono-num text-[var(--ink-muted)]">{last ? `${last.label} · ${formatDate(last.taken_at).replace(",", "")}` : "Sin snapshots"}</div>
        </div>
        <div className="border border-[var(--border)] bg-white p-5">
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Variación vs anterior</div>
          <div className={`font-display font-black text-xl mt-1 inline-flex items-center gap-1 ${delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {delta >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
            {prev ? `${delta >= 0 ? "+" : ""}${formatEURDecimal(delta)}` : "—"}
          </div>
          <div className="text-[10px] font-mono-num text-[var(--ink-muted)]">{prev ? prev.label : ""}</div>
        </div>
        <div className="border border-[var(--border)] bg-white p-5">
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Saldo bancos hoy</div>
          <div className="font-display font-black text-xl mt-1">{last ? formatEURDecimal(last.saldo_bancos) : "—"}</div>
        </div>
        <div className="border border-[var(--border)] bg-white p-5">
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Snapshots guardados</div>
          <div className="font-display font-black text-xl mt-1">{snaps.length}</div>
        </div>
      </div>

      {/* Chart */}
      <div className="border border-[var(--border)] bg-white p-6 mb-6">
        <div className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)] mb-1">Cashflow proyectado fin de año</div>
        <h2 className="font-display text-xl font-bold tracking-tight mb-6">Evolución semana a semana</h2>
        {snaps.length === 0 ? (
          <div className="text-center py-12 text-sm text-[var(--ink-muted)]" data-testid="snap-empty">
            Aún no hay snapshots. Pulsa «Snapshot ahora» para guardar el estado actual.
          </div>
        ) : (
          <div className="relative">
            <div className="h-56 flex items-end gap-1 border-b border-[var(--border)]">
              {snaps.map((s, i) => {
                const h = (Math.abs(s.cashflow_fin_anio) / maxAbs) * 100;
                const pos = s.cashflow_fin_anio >= 0;
                return (
                  <div key={s.id} className="flex-1 flex flex-col items-center justify-end h-full group min-w-[14px]" title={`${s.label}: ${formatEURDecimal(s.cashflow_fin_anio)}`}>
                    <div
                      className={`w-full transition-all ${pos ? "bg-emerald-700 hover:bg-emerald-600" : "bg-red-700 hover:bg-red-600"}`}
                      style={{ height: `${h}%`, minHeight: 1 }}
                      data-testid={`snap-bar-${i}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex gap-1 mt-2">
              {snaps.map((s, i) => (
                <div key={i} className="flex-1 text-center text-[9px] font-mono-num text-[var(--ink-muted)] min-w-[14px] truncate">{i % Math.ceil(snaps.length / 12) === 0 ? s.label.slice(5) : ""}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border border-[var(--border)] bg-white">
        <div className="px-5 py-3 border-b border-[var(--border)] font-mono-num text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)]">
          Historial de snapshots ({snaps.length})
        </div>
        <div className="overflow-auto max-h-[55vh]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--hover)] text-[10px] uppercase tracking-widest font-mono-num text-[var(--ink-muted)] sticky top-0">
              <tr>
                <th className="text-left px-4 py-3">Semana</th>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="text-right px-4 py-3">Saldo bancos</th>
                <th className="text-right px-4 py-3">Ingresos real</th>
                <th className="text-right px-4 py-3">Previstos</th>
                <th className="text-right px-4 py-3">Gastos proy</th>
                <th className="text-right px-4 py-3">Gastos fijos</th>
                <th className="text-right px-4 py-3">Cashflow fin año</th>
                <th className="text-right px-4 py-3">Origen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" className="px-4 py-6 text-center text-[var(--ink-muted)]">Cargando…</td></tr>
              ) : snaps.length === 0 ? (
                <tr><td colSpan="10" className="px-4 py-6 text-center text-[var(--ink-muted)]">Sin snapshots</td></tr>
              ) : [...snaps].reverse().map(s => (
                <tr key={s.id} className="border-t border-[var(--border)] hover:bg-[var(--hover)]" data-testid={`snap-row-${s.id}`}>
                  <td className="px-4 py-2 font-mono-num text-xs font-bold">{s.label}</td>
                  <td className="px-4 py-2 font-mono-num text-xs text-[var(--ink-muted)]">{formatDate(s.taken_at).replace(",", "")}</td>
                  <td className="px-4 py-2 text-right font-mono-num">{formatEURDecimal(s.saldo_bancos)}</td>
                  <td className="px-4 py-2 text-right font-mono-num text-emerald-700">{formatEURDecimal(s.total_ingresos_real)}</td>
                  <td className="px-4 py-2 text-right font-mono-num text-emerald-700/60 italic">~{formatEURDecimal(s.total_ingresos_previsto)}</td>
                  <td className="px-4 py-2 text-right font-mono-num text-red-700">−{formatEURDecimal(s.total_gastos_proyectos)}</td>
                  <td className="px-4 py-2 text-right font-mono-num text-red-700">−{formatEURDecimal(s.total_gastos_fijos)}</td>
                  <td className={`px-4 py-2 text-right font-mono-num font-bold ${s.cashflow_fin_anio >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {formatEURDecimal(s.cashflow_fin_anio)}
                  </td>
                  <td className="px-4 py-2 text-right text-[10px] font-mono-num text-[var(--ink-muted)]">{s.auto ? "AUTO" : "MANUAL"}</td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => remove(s.id)} className="text-[var(--ink-muted)] hover:text-red-700" data-testid={`snap-delete-${s.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
